import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "~/lib/utils";
import { Picture } from "~/components/ui/picture";

const fillVariants = cva("", {
  variants: {
    fill: {
      navy: "bg-bouncie-900",
      blue: "bg-card-fill-blue",
      "light-blue": "bg-card-fill-light-blue",
      grey: "bg-card-fill-grey",
      pink: "bg-card-pink",
      teal: "bg-card-teal",
      green: "bg-card-green",
      "light-green": "bg-card-light-green",
      yellow: "bg-card-yellow",
      "yellow-alt": "bg-card-yellow-alt",
      orange: "bg-card-orange",
      white: "bg-card-white",
    },
  },
});

export type HighlightCardFill = NonNullable<VariantProps<typeof fillVariants>["fill"]>;

export type HighlightCardProps =
  | { useCase: "color"; fill: HighlightCardFill; children: React.ReactNode; className?: string; style?: React.CSSProperties }
  | {
      useCase: "imagery";
      header?: string;
      image: string;
      /** CSS object-position for `image`, for a source whose focal point isn't centered. */
      imagePosition?: string;
      className?: string;
      style?: React.CSSProperties;
    };

function HighlightCard(props: HighlightCardProps) {
  const { useCase, className, style } = props;
  return (
    <div
      className={cn(
        "relative flex h-[450px] w-[310px] flex-col items-start rounded-2xl p-8 shadow-sm lg:h-[525px] lg:w-[400px]",
        useCase === "color" && fillVariants({ fill: props.fill }),
        className,
      )}
      // A plain className override (h-full/w-full) loses to HighlightCard's own `lg:h-*`
      // at desktop widths — Tailwind emits responsive variants after base utilities
      // regardless of prop order — so a fixed-size override goes through inline style,
      // which always wins over any class-based rule.
      style={style}
    >
      {useCase === "imagery" && (
        <>
          <div aria-hidden className="absolute inset-0 overflow-hidden rounded-2xl">
            <Picture
              src={props.image}
              alt=""
              className="size-full object-cover"
              style={props.imagePosition ? { objectPosition: props.imagePosition } : undefined}
            />
            {props.header ? (
              <div className="absolute inset-0 bg-gradient-to-b from-black/15 from-[6.667%] to-black/75 to-[89.524%]" />
            ) : (
              <div className="absolute inset-0 bg-[rgba(92,92,92,0.2)]" />
            )}
          </div>
          {props.header && (
            <p className="text-text-heading-alt font-display text-md relative z-10 mt-auto w-full leading-[1.15] font-extrabold lg:text-xl">
              {props.header}
            </p>
          )}
        </>
      )}
      {useCase === "color" && props.children}
    </div>
  );
}

export { HighlightCard };
